import React, { useState, useRef, useEffect } from "react";
import { Calendar as CalendarIcon, ChevronDown } from "lucide-react";
import Calendar from "react-calendar";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { dateFnsLocale, dateLocale, isEnglish } from "i18n";
import "./styles.css";

/**
 * DatePicker - A styled date picker component
 *
 * @param {Date} value - Currently selected date
 * @param {Function} onChange - Callback when date changes
 * @param {string} placeholder - Placeholder text when no date selected
 * @param {string} className - Additional classes for the trigger button
 * @param {boolean} disabled - Whether the picker is disabled
 * @param {Date} maxDate - Maximum selectable date
 * @param {Date} minDate - Minimum selectable date
 * @param {string} displayFormat - Format string for displaying the date (default: "MMM d, yyyy")
 */
const DatePicker = ({
  value,
  onChange,
  placeholder,
  className = "",
  disabled = false,
  maxDate,
  minDate,
  displayFormat,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const pickerRef = useRef(null);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const handleDateChange = (date) => {
    onChange(date);
    setIsOpen(false);
  };

  const displayValue = value
    ? format(value, displayFormat || (isEnglish() ? "MMM d, yyyy" : "PP"), {
        locale: dateFnsLocale(),
      })
    : null;

  return (
    <div ref={pickerRef} className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`flex w-full items-center justify-between rounded-[10px] border px-4 py-3 text-left text-sm outline-none transition-all duration-150 ${
          disabled
            ? "cursor-not-allowed border-[#E5E7EB] bg-[#F9FAFB] text-[#9CA3AF]"
            : isOpen
            ? "border-[#E07A3A] bg-white"
            : "border-[#E5E7EB] bg-white hover:border-[#D1D5DB]"
        }`}
        style={{
          boxShadow: isOpen ? "0 0 0 3px rgba(224, 122, 58, 0.1)" : "none",
        }}
      >
        <div className="flex items-center gap-2">
          <CalendarIcon size={16} className="text-[#9CA3AF]" />
          <span className={displayValue ? "text-[#111827]" : "text-[#9CA3AF]"}>
            {displayValue ||
              (placeholder ?? t("navigation.datePicker.placeholder"))}
          </span>
        </div>
        <ChevronDown
          size={16}
          className={`text-[#9CA3AF] transition-transform duration-150 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Calendar Dropdown */}
      {isOpen && (
        <div
          className="absolute z-50 mt-1 overflow-hidden rounded-lg border border-[#F3F4F6] bg-white shadow-lg"
          style={{
            animation: "datePickerFadeIn 0.15s ease-out",
          }}
        >
          <div className="p-3">
            <Calendar
              className="custom-date-picker"
              value={value}
              onChange={handleDateChange}
              maxDate={maxDate}
              minDate={minDate}
              locale={dateLocale(undefined)}
              prev2Label={null}
              next2Label={null}
            />
          </div>

          {/* Keyframe animation */}
          <style>{`
            @keyframes datePickerFadeIn {
              from {
                opacity: 0;
                transform: translateY(-4px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default DatePicker;
